/**
 * Seed data + in-memory fixtures for the legacy/migrated Team-access section
 * (Users + API tokens — FRO-400). Shapes are copied verbatim from the captured
 * wire truth in
 * `openspec/changes/fro-400-migrate-users-and-api-tokens-to-settings/research/03-browser-walkthrough.md`
 * §5 — the single ground truth both the legacy tech-ui `/team` view and the
 * migrated `/settings/users|api-tokens` tabs must accept:
 *
 * - the users list envelope is `{data, totalElements, offset, limit, filters, search}`
 *   with `filters` echoed back as `{"client":"techportal"}` (the client never sends it),
 * - the tokens list envelope is a completely different `{data, page, pageSize, pageCount, total}`
 *   with a **0-based** `page`,
 * - `POST /admin/users` answers `{"success":"true"}` (a STRING) while `PUT`/`DELETE`
 *   on the same resource answer `{"success":true}` (a boolean),
 * - `DELETE /admin/integration-api-keys/{id}` answers **201**, not 200/204,
 * - `techportal-roles` is a BARE array (no `data` wrapper),
 * - `/companies/` prepends a synthetic `id: 0` "All Customers" row that carries no
 *   `operatorId` key and is NOT counted against the page size (limit 24 -> 25 rows),
 * - `/companies/v2/` — the endpoint the create-token dialog moved to in tech-ui
 *   BIZZ-240 (`f70e76a76`) — answers `{data, count, limit, offset}`, is scoped by
 *   `cpoId` (the OPERATOR's `crmCustomerId`), and prepends no synthetic row.
 *
 * The users and tokens lists are mutable in-memory arrays so create/edit/delete
 * mutate the same instance a subsequent list re-fetch reads back (both remotes
 * share the router's single msw worker — adr/0003). A full page load resets the
 * module, so Playwright's per-test isolation gives every test the seed again.
 *
 * Every row is hand-seeded or produced by a deterministic factory: no clocks and
 * no randomness, so a legacy run and a migrated run compare row for row.
 */

/* -------------------------------------------------------------------------- */
/* Roles — GET /public/admin/techportal-roles                                 */
/* -------------------------------------------------------------------------- */

/** One entry of the bare `techportal-roles` array. No `accessLevel` here. */
interface TechportalRole {
  id: string;
  name: string;
}

/**
 * The 13 assignable roles, in the exact order the endpoint returns them
 * (research/03 §5.7) — which is also the Role dropdown's order. The four ids
 * observed on the wire are kept verbatim; the rest are stable synthetic uuids.
 */
const TECHPORTAL_ROLES: TechportalRole[] = [
  { id: "4bfecab9-49a2-43d1-bb0b-8ffa70c74fd6", name: "System Admin" },
  { id: "d8e995ff-acf9-4924-9ff4-93cd2161cd29", name: "Operator" },
  { id: "1c0b4f21-0000-4000-8000-000000000003", name: "Editor" },
  { id: "1c0b4f21-0000-4000-8000-000000000004", name: "Supporter" },
  { id: "1c0b4f21-0000-4000-8000-000000000005", name: "User" },
  { id: "42791d9a-8a1a-437f-9039-21e8e6d35b45", name: "Viewer" },
  { id: "1c0b4f21-0000-4000-8000-000000000007", name: "Installer" },
  { id: "1c0b4f21-0000-4000-8000-000000000008", name: "Spirii NOC" },
  { id: "1c0b4f21-0000-4000-8000-000000000009", name: "Spirii Editor" },
  { id: "1c0b4f21-0000-4000-8000-000000000010", name: "Spirii 1st Line" },
  { id: "8a4312af-e010-44cd-bce1-656e50daba6d", name: "Fleet Manager" },
  { id: "1c0b4f21-0000-4000-8000-000000000012", name: "Depot Manager" },
  { id: "1c0b4f21-0000-4000-8000-000000000013", name: "Fleet Operations" },
];

const ROLE_ID_BY_NAME = new Map(
  TECHPORTAL_ROLES.map((role) => [role.name, role.id])
);

/* -------------------------------------------------------------------------- */
/* Operators — GET /public/enums/?operator=1                                  */
/* -------------------------------------------------------------------------- */

/** One row of the `operators` array in the enums envelope. */
interface OperatorEnum {
  id: number;
  name: string;
  crmCustomerId: number;
}

/**
 * Operator options. `"API Operator "` keeps the **trailing space** observed in
 * dev data (research/03 §5.8) — the migration must not silently trim it, and a
 * name-matching bug shows up here rather than at the live gate.
 */
const TEAM_OPERATORS: OperatorEnum[] = [
  { id: 1, name: "Spirii ApS", crmCustomerId: 100001 },
  { id: 6, name: "Nordvolt", crmCustomerId: 100006 },
  { id: 308, name: "Harbour Charge", crmCustomerId: 100308 },
  { id: 353, name: "API Operator ", crmCustomerId: 112423 },
];

/* -------------------------------------------------------------------------- */
/* Companies — GET /public/companies/                                          */
/* -------------------------------------------------------------------------- */

/**
 * One row of the companies list. The synthetic `id: 0` row carries no
 * `operatorId` key at all, so the field is optional per row.
 */
interface Company {
  id: number;
  name: string;
  crmId: number | null;
  type: string | null;
  operatorId?: number;
}

/**
 * The server-prepended synthetic row. It has `crmId: null`, `type: null` and no
 * `operatorId`, and is excluded from `totalElements` — which is why `limit=24`
 * comes back with 25 rows.
 */
const ALL_CUSTOMERS_COMPANY: Company = {
  id: 0,
  name: "All Customers",
  crmId: null,
  type: null,
};

/** Real companies, ordered by name as the endpoint returns them. */
const TEAM_COMPANIES: Company[] = [
  {
    id: 4089,
    name: "Aalborg Logistik",
    crmId: 112607,
    type: "Business",
    operatorId: 1,
  },
  {
    id: 4079,
    name: "Bente Sørensen",
    crmId: 112626,
    type: "Private",
    operatorId: 6,
  },
  {
    id: 4082,
    name: "Carrie Oakey",
    crmId: 112064,
    type: "Private",
    operatorId: 308,
  },
  {
    id: 4090,
    name: "Depot Nord",
    crmId: 112352,
    type: "Business",
    operatorId: 1,
  },
  {
    id: 4095,
    name: "Europa Logistik Group",
    crmId: 112700,
    type: "Business",
    operatorId: 353,
  },
];

interface CompaniesQuery {
  offset: number;
  limit: number;
  operatorId?: number;
}

interface CompaniesResponse {
  data: Company[];
  totalElements: number;
  offset: number;
  limit: number;
  search: string;
  filters: Record<string, never>;
}

/**
 * Answer the companies list. The synthetic "All Customers" row is prepended
 * outside the page window and outside `totalElements`, reproducing the observed
 * off-by-one; `operatorId` narrows the real rows (the Operator -> Customer
 * cascade).
 */
function listCompanies({
  offset,
  limit,
  operatorId,
}: CompaniesQuery): CompaniesResponse {
  const scoped =
    operatorId === undefined
      ? TEAM_COMPANIES
      : TEAM_COMPANIES.filter((company) => company.operatorId === operatorId);

  return {
    data: [ALL_CUSTOMERS_COMPANY, ...scoped.slice(offset, offset + limit)],
    totalElements: scoped.length,
    offset,
    limit,
    search: "",
    filters: {},
  };
}

/* -------------------------------------------------------------------------- */
/* Customers — GET /public/companies/v2/                                       */
/* -------------------------------------------------------------------------- */

interface CustomersQuery {
  offset: number;
  limit: number;
  /** The OPERATOR's `crmCustomerId`, never its operator id. */
  cpoId?: number;
}

/** The v2 envelope: rows under `data`, total under `count` — not `totalElements`. */
interface CustomersResponse {
  data: Company[];
  count: number;
  limit: number;
  offset: number;
}

/**
 * Answer the customers list the create-token dialog reads after tech-ui
 * BIZZ-240 (`f70e76a76`). Two things are deliberately NOT like `/companies/`:
 * it is scoped by the operator's `crmCustomerId` rather than its id, and it
 * prepends NO synthetic "All Customers" row — so an implementation that sends
 * the operator id here gets an empty list rather than a plausible one.
 *
 * The cpo scope is resolved through the seeded operators, so both endpoints
 * answer the same customers for the same operator: the fixture must not invent
 * a divergence production does not have.
 */
function listCustomers({
  offset,
  limit,
  cpoId,
}: CustomersQuery): CustomersResponse {
  const scoped =
    cpoId === undefined
      ? TEAM_COMPANIES
      : TEAM_COMPANIES.filter((company) => {
          const operator = TEAM_OPERATORS.find(
            (entry) => entry.id === company.operatorId
          );
          return operator?.crmCustomerId === cpoId;
        });

  return {
    data: scoped.slice(offset, offset + limit),
    count: scoped.length,
    limit,
    offset,
  };
}

/* -------------------------------------------------------------------------- */
/* Users — GET /public/admin/users/                                            */
/* -------------------------------------------------------------------------- */

/**
 * One role entry inside a user row. `accessLevel` is a numeric-as-string on real
 * roles and `null` on the `permission_*` pseudo-roles, and pseudo-role entries
 * carry no `id` — which is exactly why a strict role schema rejects live rows.
 */
interface TeamUserRole {
  id?: string;
  name: string;
  accessLevel: string | null;
}

/**
 * One row of the users list. The field types are the OBSERVED ones, not the ones
 * the names suggest: ids and access levels arrive as numeric-as-strings,
 * `betaTester` as a boolean-as-string, `fullName` can be `""` but never null,
 * and `maxRoleAccessLevel` is a real number while `roles[].accessLevel` is not.
 */
interface TeamUser {
  id: string;
  createdAt: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string;
  username: string;
  companyId: string | null;
  company: string | null;
  operatorId: string | null;
  operator: string | null;
  betaTester: string | null;
  roles: TeamUserRole[];
  maxRoleAccessLevel: number | null;
  readOnly: boolean;
}

function role(name: string, accessLevel: string): TeamUserRole {
  return { id: ROLE_ID_BY_NAME.get(name), name, accessLevel };
}

/**
 * A plain, fully-populated row. Used for the bulk of the list so pagination and
 * sorting have enough rows to be meaningful without 26 hand-written literals;
 * every value is derived from the index, so the seed is byte-identical run to run.
 */
function seedUser({
  index,
  firstName,
  lastName,
  operatorId,
  operatorName,
  companyId,
  companyName,
  roleNames,
}: {
  index: number;
  firstName: string;
  lastName: string;
  operatorId: number;
  operatorName: string;
  companyId: number;
  companyName: string;
  roleNames: [string, string?];
}): TeamUser {
  const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`;
  const roles = roleNames
    .filter((name): name is string => name !== undefined)
    .map((name, roleIndex) => role(name, roleIndex === 0 ? "30" : "20"));

  return {
    id: `00000000-0000-4000-8000-${String(index).padStart(12, "0")}`,
    // Descending createdAt in seed order, so the array order IS the server's
    // default `createdAt desc` order.
    createdAt: `2026-07-${String(28 - index).padStart(2, "0")}T09:00:00.000Z`,
    email,
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`,
    username: email,
    companyId: String(companyId),
    company: companyName,
    operatorId: String(operatorId),
    operator: operatorName,
    betaTester: "false",
    roles,
    maxRoleAccessLevel: 30,
    readOnly: false,
  };
}

/**
 * The four dirty rows, verbatim from research/03 §5.2. They sit at the top of
 * the seed (newest first) so every spec sees them on page 1 at any page size.
 */
const DIRTY_USER_SEED: TeamUser[] = [
  {
    // The pseudo-role row: nulls everywhere the type forbids them, an empty
    // `fullName`, and `roles[]` holding raw permission strings with a null
    // access level and no id. Its `username` is a bare handle, so searching for
    // it matches a row whose Name and Email cells are both BLANK — the reason a
    // spec must never assert "the search term appears in a visible cell".
    id: "05efaa1f-0000-4000-8000-000000000001",
    createdAt: "2026-07-28T12:00:00.000Z",
    email: null,
    firstName: null,
    lastName: null,
    fullName: "",
    username: "nastya",
    companyId: null,
    company: null,
    operatorId: null,
    operator: null,
    betaTester: null,
    roles: [
      { name: "permission_company_car_view", accessLevel: null },
      { name: "permission_company_car_edit", accessLevel: null },
    ],
    maxRoleAccessLevel: null,
    readOnly: false,
  },
  {
    // Real roles but a null operator/company pair, and a real number in
    // `maxRoleAccessLevel` while its role access levels are strings.
    id: "80743a44-0000-4000-8000-000000000002",
    createdAt: "2026-07-28T11:00:00.000Z",
    email: "nicole.poel@example.com",
    firstName: "Nicole",
    lastName: "Poel",
    fullName: "Nicole Poel",
    username: "nicole.poel@example.com",
    companyId: null,
    company: null,
    operatorId: null,
    operator: null,
    betaTester: null,
    roles: [role("Supporter", "40"), role("Operator", "20")],
    maxRoleAccessLevel: 40,
    readOnly: false,
  },
  {
    // `betaTester` as the STRING "true", and the "All Operators"/"All Companies"
    // labels the server renders for the id-0 pair.
    id: "000ed071-0000-4000-8000-000000000003",
    createdAt: "2026-07-28T10:00:00.000Z",
    email: "anders.fischer@example.com",
    firstName: "Anders",
    lastName: "Fischer",
    fullName: "Anders Fischer",
    username: "anders.fischer@example.com",
    companyId: "0",
    company: "All Companies",
    operatorId: "0",
    operator: "All Operators",
    betaTester: "true",
    roles: [role("Fleet Manager", "30"), role("System Admin", "10")],
    maxRoleAccessLevel: 30,
    readOnly: false,
  },
  {
    // A null email on an otherwise-populated row: the Email cell renders blank
    // while the Name cell does not.
    id: "9b1c2d3e-0000-4000-8000-000000000004",
    createdAt: "2026-07-28T09:00:00.000Z",
    email: null,
    firstName: "Mette",
    lastName: "Holm",
    fullName: "Mette Holm",
    username: "mette-holm",
    companyId: "4090",
    company: "Depot Nord",
    operatorId: "1",
    operator: "Spirii ApS",
    betaTester: "false",
    roles: [role("Installer", "50")],
    maxRoleAccessLevel: 50,
    readOnly: false,
  },
];

/**
 * 22 plain rows after the 4 dirty ones = **26 users**, so the list pages at
 * every offered page size (10 -> 3 pages, 24 -> 2 pages) and page 2 is never
 * empty.
 *
 * Exactly three of them share the surname **Toft**, so a search for it asserts a
 * PLURAL match count. That matters: legacy renders the singular as "Found one
 * result" (i18next `_one`) while the migrated tab pluralizes it as "Found 1
 * result", so only a plural case can be asserted by one parity spec.
 */
const PLAIN_USER_SEED: TeamUser[] = [
  ["Alma", "Bech", 1, "Spirii ApS", 4089, "Aalborg Logistik", "Viewer"],
  ["Bo", "Clausen", 1, "Spirii ApS", 4090, "Depot Nord", "Editor"],
  ["Cille", "Dahl", 6, "Nordvolt", 4079, "Bente Sørensen", "Supporter"],
  ["Dennis", "Ebbesen", 6, "Nordvolt", 4079, "Bente Sørensen", "Operator"],
  ["Ella", "Frost", 308, "Harbour Charge", 4082, "Carrie Oakey", "Viewer"],
  ["Frida", "Gade", 308, "Harbour Charge", 4082, "Carrie Oakey", "Installer"],
  [
    "Gustav",
    "Hansen",
    353,
    "API Operator ",
    4095,
    "Europa Logistik Group",
    "Fleet Manager",
  ],
  [
    "Hanne",
    "Iversen",
    353,
    "API Operator ",
    4095,
    "Europa Logistik Group",
    "Depot Manager",
  ],
  ["Ivan", "Jensen", 1, "Spirii ApS", 4089, "Aalborg Logistik", "Viewer"],
  ["Jonas", "Krogh", 1, "Spirii ApS", 4089, "Aalborg Logistik", "Editor"],
  ["Karla", "Toft", 6, "Nordvolt", 4079, "Bente Sørensen", "Viewer"],
  ["Lars", "Madsen", 6, "Nordvolt", 4079, "Bente Sørensen", "Supporter"],
  ["Maja", "Nielsen", 308, "Harbour Charge", 4082, "Carrie Oakey", "Viewer"],
  ["Nikolaj", "Olsen", 308, "Harbour Charge", 4082, "Carrie Oakey", "Operator"],
  [
    "Oda",
    "Pedersen",
    353,
    "API Operator ",
    4095,
    "Europa Logistik Group",
    "Viewer",
  ],
  ["Peter", "Toft", 1, "Spirii ApS", 4090, "Depot Nord", "Installer"],
  ["Rikke", "Toft", 1, "Spirii ApS", 4090, "Depot Nord", "Viewer"],
  ["Simon", "Sand", 6, "Nordvolt", 4079, "Bente Sørensen", "Editor"],
  ["Tove", "Thomsen", 308, "Harbour Charge", 4082, "Carrie Oakey", "Viewer"],
  ["Ulla", "Vang", 353, "API Operator ", 4095, "Europa Logistik Group", "User"],
  ["Villads", "Winther", 1, "Spirii ApS", 4089, "Aalborg Logistik", "Viewer"],
  ["Yrsa", "Zahle", 1, "Spirii ApS", 4090, "Depot Nord", "Spirii NOC"],
].map(
  (
    [
      firstName,
      lastName,
      operatorId,
      operatorName,
      companyId,
      companyName,
      roleName,
    ],
    index
  ) =>
    seedUser({
      index: index + 5,
      firstName: firstName as string,
      lastName: lastName as string,
      operatorId: operatorId as number,
      operatorName: operatorName as string,
      companyId: companyId as number,
      companyName: companyName as string,
      roleNames: [roleName as string],
    })
);

const USER_SEED: TeamUser[] = [...DIRTY_USER_SEED, ...PLAIN_USER_SEED];

let users: TeamUser[] = USER_SEED.map((user) => ({ ...user }));

/** Sort keys the users endpoint accepts as `sort_key`. */
type UserSortKey = "createdAt" | "operator" | "company" | "fullName" | "email";

interface UsersQuery {
  offset: number;
  limit: number;
  sortKey: UserSortKey;
  sortDir: "asc" | "desc";
  search?: string;
  roleId?: string;
  operatorId?: string;
  companyId?: string;
}

interface UsersResponse {
  data: TeamUser[];
  totalElements: number;
  offset: number;
  limit: number;
  filters: { client: string };
  search: string;
}

/**
 * The server matches free text against `username` as well as the rendered
 * name/email — the reason a search can return a row with blank Name and Email
 * cells. Case-insensitive, matching the legacy Cypress spec's lower/upper cases.
 */
function matchesSearch(user: TeamUser, search: string): boolean {
  const needle = search.toLowerCase();
  return [user.fullName, user.email, user.username].some((value) =>
    (value ?? "").toLowerCase().includes(needle)
  );
}

/** Nulls sort last in both directions, so a null-operator row never leads. */
function compareNullable(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null || a === "") return 1;
  if (b === null || b === "") return -1;
  return a.localeCompare(b);
}

function sortValue(user: TeamUser, sortKey: UserSortKey): string | null {
  if (sortKey === "createdAt") return user.createdAt;
  if (sortKey === "operator") return user.operator;
  if (sortKey === "company") return user.company;
  if (sortKey === "fullName") return user.fullName;
  return user.email;
}

/** Answer the users list: filter, then search, then sort, then page. */
function listUsers({
  offset,
  limit,
  sortKey,
  sortDir,
  search,
  roleId,
  operatorId,
  companyId,
}: UsersQuery): UsersResponse {
  let matched = users;

  if (roleId) {
    matched = matched.filter((user) =>
      user.roles.some((entry) => entry.id === roleId)
    );
  }
  if (operatorId) {
    matched = matched.filter((user) => user.operatorId === operatorId);
  }
  if (companyId) {
    matched = matched.filter((user) => user.companyId === companyId);
  }
  if (search) {
    matched = matched.filter((user) => matchesSearch(user, search));
  }

  const sorted = [...matched].sort((a, b) => {
    const comparison = compareNullable(
      sortValue(a, sortKey),
      sortValue(b, sortKey)
    );
    // Nulls stay last regardless of direction: only real values flip.
    if (comparison === 0) return 0;
    const aValue = sortValue(a, sortKey);
    const bValue = sortValue(b, sortKey);
    if (aValue === null || aValue === "" || bValue === null || bValue === "") {
      return comparison;
    }
    return sortDir === "asc" ? comparison : -comparison;
  });

  const page = sorted.slice(offset, offset + limit);

  return {
    data: page,
    // Reproduced, not normalised: the real endpoint answers an OUT-OF-RANGE offset
    // with `totalElements: 0` rather than the true match count. Returning the true
    // count here made the mock kinder than production and hid a live defect — the
    // page-correction effect read `total === 0` as "empty list" and bailed, stranding
    // a reader who arrived on a stale page with no pager to escape by.
    totalElements: offset > 0 && page.length === 0 ? 0 : matched.length,
    offset,
    limit,
    filters: { client: "techportal" },
    search: search ?? "",
  };
}

interface CreateUserBody {
  operatorId: number;
  companyId: number;
  email: string;
  firstName: string;
  lastName: string;
  roleIds: string[];
  // Numbers, not booleans: what the endpoint's whitelist takes (FRO-477).
  sendEmail?: 0 | 1;
  betaTester?: 0 | 1;
}

/** Email that already belongs to a seeded account (duplicate-email spec). */
const EXISTING_USER_EMAIL = "anders.fischer@example.com";

/**
 * Email that makes the create endpoint answer the wire-validation 400 captured in
 * FRO-477 — the shape whose top-level message names nothing and whose `details[]`
 * carry the only readable explanation. A sentinel because the fixed client can no
 * longer provoke one, and the dialog's rendering of it still has to be assertable.
 */
const WIRE_REJECTED_USER_EMAIL = "e2e-wire-rejected@example.com";

function operatorLabel(operatorId: number): string | null {
  if (operatorId === 0) return "All Operators";
  return TEAM_OPERATORS.find((o) => o.id === operatorId)?.name ?? null;
}

function companyLabel(companyId: number): string | null {
  if (companyId === 0) return "All Companies";
  return TEAM_COMPANIES.find((c) => c.id === companyId)?.name ?? null;
}

function rolesFromIds(roleIds: string[]): TeamUserRole[] {
  return roleIds.map((id, index) => {
    const name = TECHPORTAL_ROLES.find((r) => r.id === id)?.name ?? id;
    return { id, name, accessLevel: index === 0 ? "30" : "20" };
  });
}

/**
 * Append a created user so the list re-fetch reads it back. The `data` wrapper
 * mirrors the observed create response, whose row shape (an `attributes` bag,
 * `roles: null`) differs from a list row.
 */
function createUser(body: CreateUserBody) {
  const id = `11111111-0000-4000-8000-${String(users.length + 1).padStart(12, "0")}`;
  users.unshift({
    id,
    createdAt: "2026-07-29T09:00:00.000Z",
    email: body.email,
    firstName: body.firstName,
    lastName: body.lastName,
    fullName: `${body.firstName} ${body.lastName}`,
    username: body.email,
    companyId: String(body.companyId),
    company: companyLabel(body.companyId),
    operatorId: String(body.operatorId),
    operator: operatorLabel(body.operatorId),
    betaTester: body.betaTester ? "true" : "false",
    roles: rolesFromIds(body.roleIds),
    maxRoleAccessLevel: 30,
    readOnly: false,
  });

  return {
    success: "true",
    data: [
      {
        id,
        email: body.email,
        firstName: body.firstName,
        lastName: body.lastName,
        username: body.email,
        attributes: {
          permittedBetaTest: body.betaTester ? "true" : "false",
          permittedCompanyId: String(body.companyId),
          permittedOperatorId: String(body.operatorId),
        },
        roles: null,
      },
    ],
  };
}

/** The edit request carries only the CHANGED text fields plus four always-sent ones. */
interface EditUserBody {
  firstName?: string;
  lastName?: string;
  roleIds: string[];
  operatorId: number;
  companyId: number;
  betaTester?: 0 | 1;
}

function editUser(id: string, body: EditUserBody): void {
  const user = users.find((candidate) => candidate.id === id);
  if (!user) return;

  if (body.firstName !== undefined) user.firstName = body.firstName;
  if (body.lastName !== undefined) user.lastName = body.lastName;
  user.fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");
  user.roles = rolesFromIds(body.roleIds);
  user.operatorId = String(body.operatorId);
  user.operator = operatorLabel(body.operatorId);
  user.companyId = String(body.companyId);
  user.company = companyLabel(body.companyId);
  user.betaTester = body.betaTester ? "true" : "false";
}

function deleteUser(id: string): void {
  users = users.filter((user) => user.id !== id);
}

/* -------------------------------------------------------------------------- */
/* Credentials — GET /public/admin/users/{id}/credentials                      */
/* -------------------------------------------------------------------------- */

/**
 * A Keycloak credential record. `credentialData` is a JSON **string**, not an
 * object, and `userLabel` is optional — Reset MFA branches on whether an `otp`
 * entry is present.
 */
interface UserCredential {
  id: string;
  type: string;
  createdDate: number;
  userLabel?: string;
  credentialData: string;
}

/**
 * Only the seeded users listed here hold credentials. `Nicole Poel` has an `otp`
 * entry (Reset MFA is offered); everyone else answers `{"data":[]}`, which is the
 * legacy silent no-op the migration replaces with a visibly disabled item.
 */
const USER_CREDENTIALS: Record<string, UserCredential[]> = {
  "80743a44-0000-4000-8000-000000000002": [
    {
      id: "ed7ad4d1-0000-4000-8000-000000000001",
      type: "password",
      createdDate: 1762781683193,
      credentialData:
        '{"hashIterations":5,"algorithm":"argon2","additionalParameters":{}}',
    },
    {
      id: "d622d4d5-0000-4000-8000-000000000002",
      type: "otp",
      createdDate: 1752064392029,
      credentialData:
        '{"subType":"totp","digits":6,"counter":0,"period":30,"algorithm":"HmacSHA1"}',
    },
  ],
};

let credentials: Record<string, UserCredential[]> = {
  ...USER_CREDENTIALS,
};

function listUserCredentials(userId: string): { data: UserCredential[] } {
  return { data: credentials[userId] ?? [] };
}

/** Reset MFA removes the credential; a subsequent fetch no longer offers it. */
function deleteUserCredential(userId: string, credentialId: string): void {
  const existing = credentials[userId];
  if (!existing) return;
  credentials = {
    ...credentials,
    [userId]: existing.filter((entry) => entry.id !== credentialId),
  };
}

/* -------------------------------------------------------------------------- */
/* API tokens — GET /public/admin/integration-api-keys/                        */
/* -------------------------------------------------------------------------- */

/** The nested operator/company objects a token row carries — either can be null. */
interface ApiTokenParty {
  id: number;
  crmId?: number | null;
  name: string | null;
}

/** One row of the tokens list. */
interface ApiToken {
  id: number;
  label: string;
  company: ApiTokenParty | null;
  operator: ApiTokenParty | null;
  whiteLabel: null;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * The three edge-case rows, verbatim from research/03 §5.10, followed by 23
 * plain ones (**26 tokens**, so the tab pages at every page size).
 *
 * Ids are deliberately NOT in list order: 92 precedes 94 as observed, so a spec
 * or an implementation that assumes id ordering fails here rather than in prod.
 */
const TOKEN_SEED: ApiToken[] = [
  {
    id: 128,
    label: "Testing cloud emulator",
    company: { id: 4090, crmId: 112352, name: "Depot Nord" },
    operator: { id: 1, name: "Spirii ApS" },
    whiteLabel: null,
    contactName: "Niklas Luettringhaus",
    contactEmail: "niklas@example.com",
    contactPhone: "+004591410807",
    createdAt: "2026-07-22T11:52:48.207Z",
    updatedAt: "2026-07-22T11:52:48.207Z",
  },
  {
    // An "All Customers" token: `company.name` is null, so the Customer cell
    // renders BLANK here — unlike the Users tab, which shows "All Companies".
    id: 127,
    label: "Security testing with Qualys",
    company: { id: 0, crmId: null, name: null },
    operator: { id: 1, name: "Spirii ApS" },
    whiteLabel: null,
    contactName: "Anton Schmidt",
    contactEmail: "anton@example.com",
    contactPhone: "+4531247044",
    createdAt: "2026-07-08T11:00:48.384Z",
    updatedAt: "2026-07-08T11:00:48.384Z",
  },
  {
    // The empty-label token: its delete gate has nothing to type
    // (research/03 §8.4), and the Label cell renders blank.
    id: 74,
    label: "",
    company: null,
    operator: null,
    whiteLabel: null,
    contactName: "Unknown Contact",
    contactEmail: "unknown@example.com",
    contactPhone: "11223344",
    createdAt: "2026-04-04T11:43:00.000Z",
    updatedAt: "2026-04-04T11:43:00.000Z",
  },
  // Adjacent, out-of-order ids created in the same minute — the observed
  // arbitrary tie-break the migrated table must not "correct".
  {
    id: 92,
    label: "Fleet sync",
    company: { id: 4089, crmId: 112607, name: "Aalborg Logistik" },
    operator: { id: 1, name: "Spirii ApS" },
    whiteLabel: null,
    contactName: "Alma Bech",
    contactEmail: "alma@example.com",
    contactPhone: "+4530000092",
    createdAt: "2026-04-04T11:43:00.000Z",
    updatedAt: "2026-04-04T11:43:00.000Z",
  },
  {
    id: 94,
    label: "Depot telemetry",
    company: { id: 4090, crmId: 112352, name: "Depot Nord" },
    operator: { id: 1, name: "Spirii ApS" },
    whiteLabel: null,
    contactName: "Bo Clausen",
    contactEmail: "bo@example.com",
    contactPhone: "+4530000094",
    createdAt: "2026-04-04T11:43:00.000Z",
    updatedAt: "2026-04-04T11:43:00.000Z",
  },
  ...Array.from({ length: 21 }, (_, index) => {
    const id = 100 + index;
    const operator = TEAM_OPERATORS[index % TEAM_OPERATORS.length];
    const company = TEAM_COMPANIES[index % TEAM_COMPANIES.length];
    return {
      id,
      label: `Integration ${id}`,
      company: { id: company.id, crmId: company.crmId, name: company.name },
      operator: { id: operator.id, name: operator.name },
      whiteLabel: null,
      contactName: `Contact ${id}`,
      contactEmail: `contact-${id}@example.com`,
      contactPhone: `+45300001${String(index).padStart(2, "0")}`,
      createdAt: `2026-03-${String(28 - index).padStart(2, "0")}T09:00:00.000Z`,
      updatedAt: `2026-03-${String(28 - index).padStart(2, "0")}T09:00:00.000Z`,
    } satisfies ApiToken;
  }),
];

let tokens: ApiToken[] = TOKEN_SEED.map((token) => ({ ...token }));

interface TokensQuery {
  /** 0-based, unlike the users list's derived offset. */
  page: number;
  pageSize: number;
  search?: string;
}

interface TokensResponse {
  data: ApiToken[];
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
}

/**
 * Which token fields a free-text search matches. The live endpoint has never
 * accepted `search` (research/03 §5.11 — it answers a Joi 400), so unlike the
 * users list's `matchesSearch` this field set is ASSUMED, not observed: it is the
 * row's visible TEXT, case-insensitively, minus the numeric ID column, where a
 * substring match would make "1" hit almost everything. The real field set is on
 * the live checklist.
 */
function matchesTokenSearch(token: ApiToken, search: string): boolean {
  const needle = search.toLowerCase();
  return [
    token.label,
    token.operator?.name,
    token.company?.name,
    token.contactName,
    token.contactEmail,
    token.contactPhone,
  ].some((value) => (value ?? "").toLowerCase().includes(needle));
}

/** Answer the tokens list: search, then page — never re-sorted by id. */
function listApiTokens({
  page,
  pageSize,
  search,
}: TokensQuery): TokensResponse {
  const matched = search
    ? tokens.filter((token) => matchesTokenSearch(token, search))
    : tokens;
  const start = page * pageSize;
  return {
    data: matched.slice(start, start + pageSize),
    page,
    pageSize,
    pageCount: Math.ceil(matched.length / pageSize),
    total: matched.length,
  };
}

interface CreateApiTokenBody {
  label: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  operatorId: number;
  companyId: number | null;
}

/**
 * Create a token. The response is FLAT (not wrapped in `data`), carries the
 * secret under `key` — the only place it ever appears — and reports
 * `operatorId`/`companyId` as scalars rather than the nested objects a list row
 * carries.
 */
function createApiToken(body: CreateApiTokenBody) {
  const id = 200 + tokens.length;
  const operator = TEAM_OPERATORS.find((o) => o.id === body.operatorId);
  const company =
    body.companyId === null || body.companyId === 0
      ? null
      : TEAM_COMPANIES.find((c) => c.id === body.companyId);

  tokens.unshift({
    id,
    label: body.label,
    company: company
      ? { id: company.id, crmId: company.crmId, name: company.name }
      : null,
    operator: operator ? { id: operator.id, name: operator.name } : null,
    whiteLabel: null,
    contactName: body.contactName,
    contactEmail: body.contactEmail,
    contactPhone: body.contactPhone,
    createdAt: "2026-07-29T14:02:39.576Z",
    updatedAt: "2026-07-29T14:02:39.576Z",
  });

  return {
    id,
    key: `e2e-secret-token-${id}`,
    label: body.label,
    contactName: body.contactName,
    contactEmail: body.contactEmail,
    contactPhone: body.contactPhone,
    operatorId: body.operatorId,
    companyId: body.companyId,
    createdAt: "2026-07-29T14:02:39.576Z",
  };
}

function deleteApiToken(id: number): void {
  tokens = tokens.filter((token) => token.id !== id);
}

export {
  ALL_CUSTOMERS_COMPANY,
  EXISTING_USER_EMAIL,
  WIRE_REJECTED_USER_EMAIL,
  TEAM_COMPANIES,
  TEAM_OPERATORS,
  TECHPORTAL_ROLES,
  createApiToken,
  createUser,
  deleteApiToken,
  deleteUser,
  deleteUserCredential,
  editUser,
  listApiTokens,
  listCompanies,
  listCustomers,
  listUserCredentials,
  listUsers,
};
export type {
  ApiToken,
  Company,
  CreateApiTokenBody,
  CreateUserBody,
  EditUserBody,
  OperatorEnum,
  TeamUser,
  TeamUserRole,
  TechportalRole,
  UserCredential,
  UserSortKey,
};
